import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { RefreshCw, Archive, Lock, Snowflake, Sun, Save, Smartphone, ShieldCheck, Activity, Database } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, Card, CardHeader, CardBody, Button, Field, Input, Select, Badge, Modal, Spinner, Switch } from '../../ui';

const INTERVALS = [5, 10, 15, 30, 60, 120, 240];

export default function SystemSettingsPage() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t('system.title')} description={t('system.subtitle')} />
      <div className="grid gap-6 lg:grid-cols-2">
        <SyncCard />
        <SslCard />
        <BackupCard />
        <ThemeCard />
        <AppVersionCard />
        <ReliabilityCard />
      </div>
    </>
  );
}

function SyncCard() {
  const { t } = useI18n();
  const [interval, setIntervalValue] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/admin/cron-settings')
      .then((res) => { setIntervalValue(res.data.intervalMinutes); setSaved(res.data.intervalMinutes); })
      .catch((err) => toast.error(errorText(err, t('common.error'))));
    return () => clearInterval(pollRef.current);
  }, [t]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/admin/cron-settings', { intervalMinutes: Number(interval) });
      setSaved(Number(interval));
      toast.success(t('system.saved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const syncNow = async () => {
    try {
      await api.post('/admin/update-all-e-wallets');
      toast.success(t('system.syncStarted'));
      setProgress(0);
      clearInterval(pollRef.current);
      let seenRunning = false;
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.get('/admin/e-wallet-update-status');
          const p = Number(res.data.progress) || 0;
          if (p > 0) seenRunning = true;
          setProgress(p);
          if (p >= 100 || (seenRunning && p === 0)) {
            clearInterval(pollRef.current);
            setTimeout(() => setProgress(null), 2500);
          }
        } catch {
          clearInterval(pollRef.current);
          setProgress(null);
        }
      }, 1500);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const options = interval && !INTERVALS.includes(Number(interval)) ? [...INTERVALS, Number(interval)].sort((a, b) => a - b) : INTERVALS;

  return (
    <Card>
      <CardHeader title={t('system.sync')} description={t('system.syncHint')} />
      <CardBody className="space-y-4">
        {interval === null ? <Spinner /> : (
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t('system.interval')} className="w-40">
              <Select value={interval} onChange={(e) => setIntervalValue(e.target.value)}>
                {options.map((m) => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Field>
            <Button variant="secondary" icon={Save} onClick={save} loading={saving} disabled={Number(interval) === saved}>{t('common.save')}</Button>
          </div>
        )}
        <div className="border-t border-zinc-100 pt-4 dark:border-zinc-800">
          <Button icon={RefreshCw} onClick={syncNow} disabled={progress !== null}>{t('system.syncNow')}</Button>
          {progress !== null && (
            <div className="mt-3" role="status">
              <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div className="h-full rounded-full bg-brand-600 transition-all duration-500" style={{ width: `${Math.min(100, progress)}%` }} />
              </div>
              <p className="tabular mt-1.5 text-xs text-zinc-500">{t('system.syncProgress', { percent: Math.round(progress) })}</p>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function SslCard() {
  const { t, fmt } = useI18n();
  const [ssl, setSsl] = useState(undefined); // undefined = loading, null = unavailable
  const [log, setLog] = useState('');
  const [renewing, setRenewing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const logEnd = useRef(null);

  const load = useCallback(() => {
    api.get('/admin/ssl/status').then((res) => setSsl(res.data)).catch(() => setSsl(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { logEnd.current?.scrollIntoView({ block: 'end' }); }, [log]);

  const renew = async () => {
    setRenewing(true);
    setLog('');
    setLogOpen(true);
    try {
      const res = await fetch('/api/admin/ssl/renew-stream', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        setLog((prev) => prev + decoder.decode(value, { stream: true }));
      }
      load();
    } catch (err) {
      setLog((prev) => `${prev}\n${err.message}`);
    } finally {
      setRenewing(false);
    }
  };

  const days = ssl?.daysRemaining;
  const tone = days === undefined ? 'neutral' : days < 7 ? 'danger' : days < 21 ? 'warning' : 'success';

  return (
    <Card>
      <CardHeader
        title={t('system.ssl')}
        actions={<Button variant="secondary" size="sm" icon={Lock} onClick={renew} loading={renewing}>{t('system.sslRenew')}</Button>}
      />
      <CardBody>
        {ssl === undefined && <Spinner />}
        {ssl === null && <p className="text-sm text-zinc-500">{t('system.sslUnavailable')}</p>}
        {ssl && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Row label={t('system.sslDomain')}><span className="font-mono">{ssl.domain}</span></Row>
            <Row label={t('system.sslIssuer')}>{ssl.issuer || '—'}</Row>
            <Row label={t('system.sslValidTo')}><span className="tabular">{fmt.date(ssl.validTo)}</span></Row>
            <Row label={t('system.sslDays')}><Badge tone={tone}><span className="tabular">{days}</span></Badge></Row>
          </dl>
        )}
      </CardBody>
      <Modal
        open={logOpen}
        onClose={() => !renewing && setLogOpen(false)}
        title={t('system.sslOutput')}
        size="lg"
        footer={<Button variant="secondary" onClick={() => setLogOpen(false)} disabled={renewing}>{t('common.close')}</Button>}
      >
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-zinc-950 p-4 font-mono text-xs text-zinc-100 custom-scrollbar">
          {log || '…'}
          {renewing && <span className="animate-pulse">▍</span>}
          <span ref={logEnd} />
        </pre>
      </Modal>
    </Card>
  );
}

function BackupCard() {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      await api.post('/admin/backup');
      toast.success(t('system.backupDone'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setRunning(false);
    }
  };
  return (
    <Card>
      <CardHeader title={t('system.backup')} />
      <CardBody className="space-y-4">
        <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('system.backupHint')}</p>
        <Button variant="secondary" icon={running ? undefined : Archive} onClick={run} loading={running}>{t('system.backupNow')}</Button>
      </CardBody>
    </Card>
  );
}

function ThemeCard() {
  const { t } = useI18n();
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get('/public/config').then((res) => setTheme(res.data.theme || 'standard')).catch(() => setTheme('standard'));
  }, []);

  const choose = async (value) => {
    if (value === theme) return;
    setSaving(value);
    try {
      await api.post('/admin/settings/theme', { theme: value });
      setTheme(value);
      toast.success(t('system.themeSaved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(null);
    }
  };

  const options = [
    { id: 'standard', icon: Sun },
    { id: 'new_year', icon: Snowflake },
  ];

  return (
    <Card>
      <CardHeader title={t('system.seasonal')} description={t('system.seasonalHint')} />
      <CardBody>
        {theme === null ? <Spinner /> : (
          <div role="radiogroup" aria-label={t('system.seasonal')} className="grid grid-cols-2 gap-3">
            {options.map(({ id, icon: Icon }) => {
              const active = theme === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => choose(id)}
                  disabled={!!saving}
                  className={clsx(
                    'flex items-center gap-3 rounded-xl border p-4 text-left text-sm font-medium transition-colors',
                    active
                      ? 'border-brand-500 bg-brand-50 text-brand-800 ring-1 ring-brand-500 dark:border-brand-400 dark:bg-brand-950/40 dark:text-brand-200'
                      : 'border-zinc-200 text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800'
                  )}
                >
                  {saving === id ? <Spinner /> : <Icon className="h-5 w-5" />}
                  {t(`system.themes.${id}`)}
                </button>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Надёжность: ежедневная копия базы с проверкой и сторож доступности сайта */
function ReliabilityCard() {
  const { t, fmt } = useI18n();
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/reliability').then((res) => setData(res.data)).catch(() => setData({ backup: null, uptime: null, channels: {} }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkNow = async () => {
    setChecking(true);
    try {
      await api.post('/admin/reliability/backup-check');
      toast.success(t('reliability.backupOk'));
    } catch (err) {
      toast.error(errorText(err, t('reliability.backupFailed')));
    } finally {
      setChecking(false);
      load();
    }
  };

  const backup = data?.backup;
  const uptime = data?.uptime;
  // Сторож пишет статус раз в минуту: старше 5 минут — сторож не запущен
  const watchdogAlive = uptime?.checked_at && Date.now() - new Date(uptime.checked_at).getTime() < 5 * 60000;
  const size = backup?.size ? `${(backup.size / 1048576).toFixed(1)} MB` : '';

  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title={t('reliability.title')}
        description={t('reliability.subtitle')}
        actions={<Button variant="secondary" size="sm" icon={Database} onClick={checkNow} loading={checking}>{t('reliability.checkNow')}</Button>}
      />
      <CardBody>
        {data === null ? <Spinner /> : (
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <ShieldCheck className={clsx('mt-0.5 h-5 w-5 shrink-0', backup?.ok ? 'text-emerald-600' : backup ? 'text-rose-600' : 'text-zinc-400')} />
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-zinc-900 dark:text-zinc-100">{t('reliability.backupTitle')}</p>
                {!backup && <p className="text-zinc-500">{t('reliability.backupNever')}</p>}
                {backup && (
                  <>
                    <p className={backup.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}>
                      {backup.ok ? t('reliability.backupOkAt', { at: fmt.dateTime(backup.at) }) : t('reliability.backupFailedAt', { at: fmt.dateTime(backup.at) })}
                    </p>
                    {backup.ok && backup.counts && (
                      <p className="mt-1 text-xs text-zinc-500">
                        {t('reliability.backupCounts', { users: fmt.number(backup.counts.users), cards: fmt.number(backup.counts.e_wallets), tx: fmt.number(backup.counts.card_transactions) })}
                        {size && ` · ${size}`}{backup.kept ? ` · ${t('reliability.kept', { count: backup.kept })}` : ''}
                      </p>
                    )}
                    {!backup.ok && <p className="mt-1 text-xs text-rose-600">{backup.error}</p>}
                  </>
                )}
                <p className="mt-1 text-xs text-zinc-500">{t('reliability.backupSchedule')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Activity className={clsx('mt-0.5 h-5 w-5 shrink-0', !watchdogAlive ? 'text-zinc-400' : uptime.ok ? 'text-emerald-600' : 'text-rose-600')} />
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-zinc-900 dark:text-zinc-100">{t('reliability.uptimeTitle')}</p>
                {!watchdogAlive && <p className="text-zinc-500">{t('reliability.watchdogOff')}</p>}
                {watchdogAlive && (
                  <p className={uptime.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}>
                    {uptime.ok ? t('reliability.siteUp', { ms: uptime.response_ms }) : t('reliability.siteDown', { since: fmt.dateTime(uptime.down_since) })}
                  </p>
                )}
                {uptime?.last_incident && (
                  <p className="mt-1 text-xs text-zinc-500">{t('reliability.lastIncident', { at: fmt.dateTime(uptime.last_incident.from), minutes: uptime.last_incident.minutes })}</p>
                )}
                <p className="mt-1 text-xs text-zinc-500">{t('reliability.uptimeHint')}</p>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function AppVersionCard() {
  const { t, fmt } = useI18n();
  const [form, setForm] = useState(null);
  const [store, setStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const apply = (data) => {
    setForm({
      min_version: data.min_version || '',
      latest_version: data.manual_latest || '',
      app_store_url: data.app_store_url || '',
      auto_latest: data.auto_latest !== false,
    });
    setStore(data.store || null);
  };

  useEffect(() => {
    api.get('/admin/settings/app-version').then((res) => apply(res.data)).catch((err) => toast.error(errorText(err, t('common.error'))));
  }, [t]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/admin/settings/app-version', form);
      apply(res.data);
      toast.success(t('system.saved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/admin/settings/app-version/refresh');
      setStore(res.data.store || null);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setRefreshing(false);
    }
  };

  const bind = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });

  return (
    <Card>
      <CardHeader
        title={t('system.appVersion')}
        description={t('system.appVersionHint')}
        actions={<Button variant="secondary" size="sm" icon={RefreshCw} onClick={refresh} loading={refreshing}>{t('system.storeRefresh')}</Button>}
      />
      <CardBody>
        {form === null ? <Spinner /> : (
          <form onSubmit={save} className="space-y-4">
            <div className="rounded-lg bg-zinc-50 px-3 py-2.5 text-sm dark:bg-zinc-800/50">
              {store?.version ? (
                <>
                  <p className="font-medium text-zinc-900 dark:text-zinc-100">{t('system.storeVersion', { version: store.version })}</p>
                  <p className="text-xs text-zinc-500">
                    {store.released_at ? t('system.storeReleased', { date: fmt.date(store.released_at) }) + ' · ' : ''}
                    {t('system.storeChecked', { at: fmt.dateTime(store.checked_at) })}
                  </p>
                </>
              ) : (
                <p className="text-zinc-500">{store?.error || t('system.storeUnknown')}</p>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
              <div>
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('system.autoLatest')}</p>
                <p className="text-xs text-zinc-500">{t('system.autoLatestHint')}</p>
              </div>
              <Switch checked={form.auto_latest} onChange={(v) => setForm({ ...form, auto_latest: v })} label={t('system.autoLatest')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('system.minVersion')} hint={t('system.minVersionHint')}><Input placeholder="1.5" {...bind('min_version')} /></Field>
              <Field label={t('system.latestVersion')} hint={form.auto_latest ? t('system.latestAutoHint') : t('system.latestVersionHint')}>
                <Input placeholder={store?.version || '1.6'} {...bind('latest_version')} disabled={form.auto_latest} />
              </Field>
            </div>
            <Field label={t('system.appStoreUrl')} hint={store?.url && !form.app_store_url ? t('system.storeUrlAuto', { url: store.url }) : null}>
              <Input type="url" placeholder={store?.url || 'https://apps.apple.com/app/id…'} {...bind('app_store_url')} />
            </Field>
            <Button type="submit" variant="secondary" icon={Smartphone} loading={saving}>{t('common.save')}</Button>
          </form>
        )}
      </CardBody>
    </Card>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className="mt-0.5 text-zinc-900 dark:text-zinc-100">{children}</dd>
    </div>
  );
}
